'use client';

import { cn } from '@ps/ui/utils';
import { Minus, Plus } from 'lucide-react';

type QuantitySelectorProps = {
  quantity: number;
  maxQuantity: number;
  isSoldOut?: boolean;
  isFixedToOne?: boolean;
  nonPurchasable?: boolean;
  onChange: (q: number) => void;
  /** Show the "Quantity" label above the stepper (RN's default). */
  showLabel?: boolean;
};

/**
 * Mirrors RN `QuantitySelector` + `CircleButtonStepper`. Two circular
 * outlined buttons flanking the current value; disabled at the min (1) and
 * max bounds.
 *
 *  - Returns `null` for fixed-quantity listings (digital, meeting, etc.).
 *  - Renders "SOLD OUT" in place of the stepper when no stock is available.
 */
export function QuantitySelector({
  quantity,
  maxQuantity,
  isSoldOut,
  isFixedToOne,
  nonPurchasable,
  onChange,
  showLabel = true,
}: QuantitySelectorProps) {
  if (isFixedToOne) return null;

  if (nonPurchasable || isSoldOut) {
    return (
      <p
        className={cn(
          'text-body px-3.5',
          nonPurchasable ? 'text-[#ff845e]' : 'text-[#4a4a4a]',
        )}
      >
        SOLD OUT
      </p>
    );
  }

  const atMin = quantity <= 1;
  const atMax = quantity >= maxQuantity;

  return (
    <div className="space-y-2.5 px-3.5">
      {showLabel ? (
        <p className="text-label font-semibold leading-[18px] text-black">
          Quantity
        </p>
      ) : null}
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={() => onChange(quantity - 1)}
          disabled={atMin}
          aria-label="Decrease quantity"
          className={cn(
            'flex size-8 items-center justify-center rounded-full border transition-colors',
            atMin
              ? 'border-zinc-200 text-zinc-300'
              : 'border-zinc-400 text-zinc-700 hover:bg-zinc-50',
          )}
        >
          <Minus className="size-4" />
        </button>
        <span className="text-body min-w-[2ch] text-center text-black">
          {quantity}
        </span>
        <button
          type="button"
          onClick={() => onChange(quantity + 1)}
          disabled={atMax}
          aria-label="Increase quantity"
          className={cn(
            'flex size-8 items-center justify-center rounded-full border transition-colors',
            atMax
              ? 'border-zinc-200 text-zinc-300'
              : 'border-zinc-400 text-zinc-700 hover:bg-zinc-50',
          )}
        >
          <Plus className="size-4" />
        </button>
      </div>
    </div>
  );
}
